import React, { useRef } from 'react';
import { ClientPhotoPurpose } from '@common/model/client/photo/purpose';
import { Box } from '@cp/ds/src/components/box';
import { Button } from '@cp/ds/src/components/button';
import { Stack } from '@cp/ds/src/components/stack';
import { PhotoPicker } from '@cp/ds/src/components/photoPicker';
import { PhotoPickerHandle } from '@cp/ds/src/components/photoPicker/model';
import { AI_IMAGE_GENERATOR_CONSTANTS, dashedBorderSx } from '../lib';
import { GenerationResultItem } from '../types';

interface GenerationResultCardProps {
  result: GenerationResultItem;
  primaryPurpose: ClientPhotoPurpose;
  secondaryPurpose: ClientPhotoPurpose;
  secondaryLabel: string;
  isSaving?: boolean;
  onSave: (purpose: ClientPhotoPurpose, croppedDataUrl?: string) => void | Promise<void | boolean>;
  onImageUrlChange: (url: string | undefined) => void;
}

const actionButtonSx = {
  flexShrink: 0,
  minWidth: 126,
} as const;

export const GenerationResultCard: React.FC<GenerationResultCardProps> = ({
  result,
  primaryPurpose,
  secondaryPurpose,
  secondaryLabel,
  isSaving,
  onSave,
  onImageUrlChange,
}) => {
  const photoPickerRef = useRef<PhotoPickerHandle>(null);
  const [savingPurpose, setSavingPurpose] = React.useState<ClientPhotoPurpose | null>(null);

  const getCropped = async () => {
    try {
      return (await photoPickerRef.current?.getCroppedImage()) || undefined;
    } catch {
      return undefined;
    }
  };

  const handleSave = async (purpose: ClientPhotoPurpose) => {
    if (result.savedPurposes?.includes(purpose)) {
      return;
    }

    setSavingPurpose(purpose);
    try {
      const cropped = await getCropped();
      await onSave(purpose, cropped);
    } finally {
      setSavingPurpose(null);
    }
  };

  const isPrimarySaved = Boolean(result.savedPurposes?.includes(primaryPurpose));
  const isSecondarySaved = Boolean(result.savedPurposes?.includes(secondaryPurpose));
  const isBusy = Boolean(isSaving || savingPurpose);

  return (
    <Box
      sx={{
        width: '100%',
        flexShrink: 0,
        minHeight: 378,
        p: 1.5,
        borderRadius: AI_IMAGE_GENERATOR_CONSTANTS.BORDER_RADIUS,
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        ...dashedBorderSx.result,
      }}
    >
      <Stack alignItems="center" direction="row" spacing={2} sx={{ width: '100%' }}>
        <Box sx={{ flexShrink: 0 }}>
          <PhotoPicker
            actionBarPosition="right"
            autoCropArea={1}
            canvasSize={AI_IMAGE_GENERATOR_CONSTANTS.PHOTO_SIZE}
            disabled={isPrimarySaved && isSecondarySaved}
            imageUrl={result.imageUrl}
            onChangeImageUrl={(url) => {
              if (!url) {
                return;
              }
              onImageUrlChange(url);
            }}
            ref={photoPickerRef}
            showDownloadButton
            sx={{ width: 'fit-content', pr: 7 }}
          />
        </Box>
        <Stack spacing={1} sx={{ ml: 'auto', flexShrink: 0 }}>
          <Button
            color="gray"
            disabled={isBusy || isPrimarySaved}
            onClick={() => handleSave(primaryPurpose)}
            size="small"
            sx={actionButtonSx}
            variant="primary"
          >
            {isPrimarySaved ? 'Сохранено' : savingPurpose === primaryPurpose ? 'Сохранение...' : 'Сохранить'}
          </Button>
          <Button
            color="gray"
            disabled={isBusy || isSecondarySaved}
            onClick={() => handleSave(secondaryPurpose)}
            size="small"
            sx={actionButtonSx}
            variant="primary"
          >
            {isSecondarySaved ? 'Сохранено' : savingPurpose === secondaryPurpose ? 'Сохранение...' : secondaryLabel}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
